/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import Slider from 'react-slick';
import { Query } from 'react-apollo';
import { useSelector } from 'react-redux';
import mbpLogger from 'mbp-logger';
import {
    useMediaQuery, Container, Grid,
} from '@material-ui/core';
import { object, func } from 'prop-types';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import withStyles from '@material-ui/core/styles/withStyles';
import { compose } from 'recompose';

// UI Components
import ZodiacBottomBanner from './Partials/ZodiacBottomBanner/ZodiacBottomBanner';
import ZodiacTopBanner from './Partials/ZodiacTopBanner/ZodiacTopBanner';
import ZodiacButtons from './Partials/ZodiacButtons/ZodiacButtons';
import ZodiacCollection from './Partials/ZodiacCollection/ZodiacCollection';
import ProductInfoDivider from '../GraphqlProductPage/GraphqlPlantsProductPageBuilder/Partials/ProductInfoDivider';

// helpers, redux, queries
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../gql';
import SimpleProductSkeleton from '../GraphqlSkeletonComponents/SimpleProductSkeleton';
import findContent from '../../../gql/queries/findContent';
import withPageView from '../../../helpers/tracking/hocs/withPageView';

const styles = (theme) => ({
    mainContainer: {
        '& .my-4': {
            marginBottom: '1.5rem',
            marginTop: '1.5rem',
        },
        '& .mt-3': {
            marginTop: '1rem',
        },
        '& img': {
            maxWidth: '100%',
            maxHeight: '100%',
        },
        '& .zodiacBottomBanner': {
            '&.padding': {
                padding: '1rem',
            },
        },
        '& .slick-arrow': {
            color: '#000',
            top: '25%',
            zIndex: 99,
        },
        '& .slick-prev': {
            left: '-10px',
        },
        '& .slick-next': {
            right: '-10px',
        },
        '& .slick-disabled': {
            display: 'none',
        },
        '& a': {
            cursor: 'pointer',
        },
        '& .productItemContainer': {
            padding: '0px 5px !important',
            marginTop: '1rem',
            '& a': {
                color: '#212324',
            },
            [theme.breakpoints.down(theme.breakpoints.maxWidth.medium_screen_up)]: {
                '& a': {
                    textDecoration: 'none',
                },
                '& .productTitle': {
                    fontSize: '0.875rem',
                    lineHeight: '0.875rem',
                },
            },
        },
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    slider_container: {
        position: 'relative',
        display: 'block',
        boxSizing: 'border-box',
        userSelect: 'none',
        touchAction: 'pan-y',
        '& .slick-arrow': {
            cursor: 'pointer',
            position: 'absolute',
            background: 'transparent',
            border: 'none',
        },
        '& .slick-prev, & .slick-next': {
            '&:before': {
                content: '""',
                fontSize: '0',
            },
        },
        '& .text-icon': {
            color: theme.palette.common?.gray_900,
        },
        '& .slick-disabled': {
            opacity: '0.4',
            display: 'none !important',
            '&:hover': {
                cursor: 'default',
            },
        },
        '& .sign-name': {
            fontSize: '1rem',
        },
        '& .slick-list': {
            overflow: 'hidden',
            margin: 0,
            padding: 0,
        },
        '& .slick-track': {
            position: 'relative',
            boxSizing: 'border-box',
            userSelect: 'none',
            touchAction: 'pan-y',
            display: 'flex',
        },
    },
});

const NextArrow = (props) => {
    const { onClick, className } = props;
    return (
        <button type="button" aria-label="Next Arrow" className={`${className}`} onClick={onClick}>
            <ChevronRightIcon className="material-icons text-icon" />
        </button>
    );
};
NextArrow.propTypes = {
    onClick: func,
    className: object.isRequired,
};

NextArrow.defaultProps = {
    onClick: () => {},
};

const PrevArrow = (props) => {
    const { onClick, className } = props;
    return (
        <button type="button" aria-label="Prev Arrow" className={`${className}`} onClick={onClick}>
            <ChevronLeftIcon className="material-icons text-icon" />
        </button>
    );
};

PrevArrow.propTypes = {
    onClick: func,
    className: object.isRequired,
};
PrevArrow.defaultProps = {
    onClick: () => {},
};

// SVG SIGNS IMPORTS
const path = 'https://images.contentstack.io/v3/assets/blt4454dc0093319d35/';

const Aries = `${path}blt6fa04c3c86898416/63ef677f0e3ec346cecd3a04/aries.svg`;
const Taurus = `${path}blt32f04036aaa2528d/63ef67947f9c3432aa4c1779/taurus.svg`;
const Gemini = `${path}bltf1e4c9329876abcc/63ef67a51cf8ea79624736b8/gemini.svg`;
const Cancer = `${path}bltcc1a544bac7db85c/63ef67bae8dab560ccbaf7ec/cancer.svg`;
const Leo = `${path}blt61a66cf822484b04/63ef67d26ad6b74b3f144156/leo.svg`;
const Virgo = `${path}bltfa7b08a681ea54f1/63ef67ecd77cb478eca7233c/virgo.svg`;
const Libra = `${path}bltc3b42db331faf12e/63ef67fb5a33d74988e9e370/libra.svg`;
const Scorpio = `${path}bltebdd922a87ce3614/63ef68085a33d74988e9e374/scorpio.svg`;
const Sagittarius = `${path}blt6208ebb7ddb5df22/63ef68197f9c3432aa4c177d/sagittarius.svg`;
const Capricorn = `${path}blt4696252e3049b5b8/63ef68274e826612ac6b5dfd/capricorn.svg`;
const Aquarius = `${path}bltcc6554cff703412c/63ef6dffd562ef129e5f047f/aquarius.svg`;
const Pisces = `${path}blt0f357a3c3f81725c/63ef684ace28003916f52b76/pisces.svg`;

const GraphqlZodiacPlants = ({ classes }) => {
    // useSelector
    const brand = useSelector(getBrand);

    const isSmallScreen = useMediaQuery('(max-width: 1024px)');

    const findContentOptions = {
        brand: brand.domain,
        environment: `${GRAPHQL_ENV}`,
        locale: 'en-us',
        contentType: 'zodiac',
    };
    const FIND_CONTENT_QUERY = findContent(findContentOptions);

    const signs = [
        { title: 'Aries', slug: 'aries', SvgImage: Aries },
        { title: 'Taurus', slug: 'taurus', SvgImage: Taurus },
        { title: 'Gemini', slug: 'gemini', SvgImage: Gemini },
        { title: 'Cancer', slug: 'cancer', SvgImage: Cancer },
        { title: 'Leo', slug: 'leo', SvgImage: Leo },
        { title: 'Virgo', slug: 'virgo', SvgImage: Virgo },
        { title: 'Libra', slug: 'libra', SvgImage: Libra },
        { title: 'Scorpio', slug: 'scorpio', SvgImage: Scorpio },
        { title: 'Sagittarius', slug: 'sagittarius', SvgImage: Sagittarius },
        { title: 'Capricorn', slug: 'capricorn', SvgImage: Capricorn },
        { title: 'Aquarius', slug: 'aquarius', SvgImage: Aquarius },
        { title: 'Pisces', slug: 'pisces', SvgImage: Pisces },
    ];

    const [selectedSign, setSelectedSign] = useState(signs[0].title);

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        swipeToSlide: true,
        slidesToScroll: 1,
        touchThreshold: 100,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
    };

    /* eslint-disable react/jsx-props-no-spreading */
    return (
        <Container className={classes.mainContainer}>
            <div className="content-wrapper">
                <div className="mb-4">
                    <ZodiacTopBanner isSmallScreen={isSmallScreen} />
                </div>
                {isSmallScreen ? (
                    <Slider className={classes.slider_container} {...settings}>
                        {signs
                            && signs.map((sign, index) => (
                                <ZodiacButtons
                                    isSmallScreen={isSmallScreen}
                                    {...sign}
                                    key={index}
                                    selectedSign={selectedSign}
                                    setSelectedSign={setSelectedSign}
                                />
                            ))}
                    </Slider>
                ) : (
                    <Grid container>
                        {signs && signs.map((sign, index) => (
                            <ZodiacButtons
                                {...sign}
                                key={index}
                                selectedSign={selectedSign}
                                isSmallScreen={isSmallScreen}
                                setSelectedSign={setSelectedSign}
                            />
                        ))}
                    </Grid>
                )}
            </div>
            <ProductInfoDivider />
            <Query query={FIND_CONTENT_QUERY} fetchPolicy="cache-and-network">
                {({ loading, data, error }) => {
                    if (loading) {
                        return <SimpleProductSkeleton />;
                    }

                    if (error) {
                        mbpLogger.logError({
                            query: FIND_CONTENT_QUERY,
                            component: 'GraphqlZodiacPlants.js',
                            message: 'Error executing findContent query',
                            env: GRAPHQL_ENV,
                            error,
                        });
                        return null;
                    }

                    if (!data) {
                        mbpLogger.logWarning({
                            query: FIND_CONTENT_QUERY,
                            component: 'GraphqlZodiacPlants.js',
                            message: 'No data returned for findContent Query',
                            env: GRAPHQL_ENV,
                        });
                        return null;
                    }
                    const entries = data.findContent?.content?.entries;
                    if (!entries) return null;
                    const signsMetaData = entries.find((item) => item.title.toLowerCase() === selectedSign.toLowerCase());
                    if (!signsMetaData) return null;
                    return <ZodiacCollection signsMetaData={signsMetaData} selectedSign={selectedSign} isSmallScreen={isSmallScreen} />;
                }}
            </Query>
            <ProductInfoDivider />
            <ZodiacBottomBanner />
        </Container>
    );
};

GraphqlZodiacPlants.propTypes = {
    classes: object.isRequired,
};

const enhance = compose(
    withStyles(styles),
    withPageView({
        // eslint-disable-next-line arrow-body-style
        pageViewSuccessTest: () => {
            return true;
        },
    }),
);

export default enhance(GraphqlZodiacPlants);
